import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Link as MuiLink,
  Snackbar,
  Alert,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import logoBenext from "../assets/logo-benext.png";
import PrivacyPolicyModal from "./PrivacyPolicyModal";

export default function Footer() {
  const { t } = useTranslation();

  const [privacyOpen, setPrivacyOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    website: "",
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, ok: true, msg: "" });

  const ENDPOINT = "/form-submit.php";

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      setToast({ open: true, ok: false, msg: t("footer.validationRequired") });
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams({
        email: form.email,
        name: form.name,
        phone: form.phone,
        company: form.company,
        message: form.message,
      }).toString();

      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const text = await res.text();
      const ok = res.ok && /sucesso|enviad[oa]/i.test(text);

      if (ok) {
        setToast({ open: true, ok: true, msg: t("footer.sentOk") });
        setForm({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
          website: "",
        });
      } else {
        throw new Error(text || t("footer.sentFail"));
      }
    } catch (err) {
      setToast({ open: true, ok: false, msg: err.message || t("footer.sentFail") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("privacy") === "1" || window.location.hash === "#privacy") {
        setPrivacyOpen(true);
      }
    } catch (e) {
    }
  }, []);

  const openPrivacy = () => {
    setPrivacyOpen(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("privacy", "1");
      window.history.replaceState(null, "", url.toString());
    } catch (e) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search + "#privacy");
    }
  };

  const closePrivacy = () => {
    setPrivacyOpen(false);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("privacy");
      if (url.hash === "#privacy") url.hash = "";
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    } catch (e) {
      if (window.location.hash === "#privacy") {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        bgcolor: "#2D2E2F",
        minHeight: 779,
        py: { xs: 6, md: 8 },
      }}
    >
      {/* Título + Subtítulo */}
      <Box
        sx={{
          width: 1000,
          maxWidth: "100%",
          mx: "auto",
          textAlign: "center",
          mb: 6,
        }}
      >
        <Typography
          component="h2"
          sx={{
            color: "#00FF4F",
            fontWeight: 500,
            lineHeight: 1.15,
            fontSize: { xs: 26, sm: 34, md: 42 },
          }}
        >
          <Trans i18nKey="footer.ctaTitle" components={{ bold: <span key="bold" style={{ fontWeight: 800 }} /> }} />
        </Typography>

        <Typography
          sx={{
            mt: 2,
            color: "#ECEDF8",
            fontSize: { xs: 14, sm: 16, md: 18 },
            opacity: 0.95,
          }}
        >
          {t("footer.ctaSubtitle")}
        </Typography>
      </Box>

      {/* Formulário */}
      <Box component="form" onSubmit={handleSubmit} sx={{ width: 1248, maxWidth: "calc(100% - 32px)", mx: "auto" }}>
        {/* Honeypot */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={handleChange("website")}
          autoComplete="off"
          tabIndex={-1}
          style={{ display: "none" }}
        />

        <Grid container columnSpacing={3} alignItems="stretch">
          {/* Coluna esquerda */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                width: { xs: "100%", md: 606 },
                mx: { xs: "auto", md: 0 },
                display: "flex",
                flexDirection: "column",
                rowGap: "24px",
              }}
            >
              <TextField
                fullWidth
                label={t("footer.name")}
                placeholder={t("footer.name")}
                type="text"
                value={form.name}
                onChange={handleChange("name")}
                variant="outlined"
                InputLabelProps={{ sx: { color: "#000000" } }}
                InputProps={{
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#0E0F10",
                    height: "46px",
                    "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.24)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.48)" },
                }}
                required
              />

              <TextField
                fullWidth
                label={t("footer.email")}
                placeholder={t("footer.email")}
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                variant="outlined"
                InputLabelProps={{ sx: { color: "#000000" } }}
                InputProps={{
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#0E0F10",
                    height: "46px",
                    "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.24)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.48)" },
                }}
                required
              />

              <TextField
                fullWidth
                label={t("footer.phone")}
                placeholder={t("footer.phone")}
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                variant="outlined"
                InputLabelProps={{ sx: { color: "#000000" } }}
                InputProps={{
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#0E0F10",
                    height: "46px",
                    "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.24)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.48)" },
                }}
              />

              <TextField
                fullWidth
                label={t("footer.company")}
                placeholder={t("footer.company")}
                type="text"
                value={form.company}
                onChange={handleChange("company")}
                variant="outlined"
                InputLabelProps={{ sx: { color: "#000000" } }}
                InputProps={{
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#0E0F10",
                    height: "46px",
                    "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.24)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.48)" },
                }}
              />
            </Box>
          </Grid>

          {/* Coluna direita */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ width: { xs: "100%", md: 606 }, mx: { xs: "auto", md: 0 }, mt: { xs: 3, md: 0 } }}>
              <TextField
                fullWidth
                multiline
                minRows={10}
                label={t("footer.message")}
                placeholder={t("footer.message")}
                value={form.message}
                onChange={handleChange("message")}
                variant="outlined"
                InputLabelProps={{ sx: { color: "#000000" } }}
                InputProps={{
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#0E0F10",
                    alignItems: "start",
                    height: { md: "256px" },
                    "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.24)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(236,237,248,0.48)" },
                }}
                required
              />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            width: "100%",
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: "#00FF4F",
              color: "#0E0F10",
              fontWeight: 700,
              textTransform: "none",
              width: 102,
              height: 46,
              borderRadius: 3,
              "&:hover": { bgcolor: "#00E347" },
            }}
          >
            {loading ? t("footer.sending") : t("footer.send")}
          </Button>
        </Box>
      </Box>

      {/* Rodapé inferior */}
      <Box
        sx={{
          width: 1248,
          maxWidth: "calc(100% - 32px)",
          mx: "auto",
          mt: { xs: 6, md: 8 },
        }}
      >
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid size={{ xs: 12, md: 2.8 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <MuiLink
                href="#hero"
                underline="none"
                sx={{ display: "inline-flex" }}
                aria-label={t("footer.backToTop")}
              >
                <Box
                  component="img"
                  src={logoBenext}
                  alt="benext logo"
                  sx={{ width: 270, height: 90, objectFit: "contain" }}
                />
              </MuiLink>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 2.8 }}>
            <Box
              sx={{
                textAlign: "start",
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                gap: 0.5,
              }}
            >
              <Typography sx={{ color: "#00FF4F", fontWeight: 700 }}>
                {t("footer.addressCity")}
              </Typography>
              <Typography sx={{ color: "#5F6368", lineHeight: 1.4, whiteSpace: "pre-line" }}>
                {t("footer.address")}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 2.2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                textAlign: "start",
                gap: 0.5,
              }}
            >
              <Typography sx={{ color: "#5F6368" }}>© Benext, LLC</Typography>

              <MuiLink
                component="button"
                type="button"
                underline="hover"
                onClick={openPrivacy}
                sx={{ color: "#5F6368", cursor: "pointer", p: 0, textAlign: "left" }}
              >
                {t("footer.privacy")}
              </MuiLink>

              <Typography sx={{ color: "#5F6368" }}>
                {t("footer.rights")}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Modal de Privacidade */}
      <PrivacyPolicyModal open={privacyOpen} onClose={closePrivacy} logoSrc={logoBenext} />

      {/* Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.ok ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
